import { createPinia } from 'pinia';
import { createApp } from 'vue';
import VueMatomo from 'vue-matomo';
import VueScrollTo from 'vue-scrollto';
import VueTheMask from 'vue-the-mask';

import * as Sentry from '@sentry/vue';
import axios from 'axios';
import moment from 'moment-timezone';
// Google Maps pour Vue 3
import { GoogleMap, Marker } from 'vue3-google-map';
// Libs with css
import { PerfectScrollbarPlugin } from 'vue3-perfect-scrollbar';
import 'vue3-perfect-scrollbar/style.css';

import ToastPlugin from 'vue-toast-notification';

import App from './App.vue';
import 'vue-toast-notification/dist/theme-default.css';

import VueVirtualScroller from 'vue-virtual-scroller';
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css';

import './scss/main.scss';

// En dev, si l’API pointe vers localhost:9080, utiliser /v1 (proxy Vite) pour éviter CORS
import { apiBaseUrl } from '@/helpers/apiBaseUrl';

import { filterSentryData, parseSampleRate } from './helpers/sentry';
import i18n from './i18n';
// Internal modules
import router from './router';
import { setupInterceptors } from './services/setupInterceptors';

axios.defaults.baseURL = apiBaseUrl;

setupInterceptors();

const app = createApp(App);

const environment = import.meta.env.VITE_APP_ENV || 'development';
if (environment !== 'development') {
	Sentry.init({
		app,
		dsn: 'https://cb4ea4294de74a8f957779d1c70325cd@o4505226881466368.ingest.us.sentry.io/4505227201544192',

		environment: import.meta.env.VITE_APP_ENV || 'development',

		integrations: [
			Sentry.browserTracingIntegration({ router }),
			Sentry.replayIntegration(),
		],

		tracesSampleRate: parseSampleRate(
			import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE
		),

		tracePropagationTargets: [
			'localhost:9050',
			'api-staging.wizim.io',
			'api-testing.wizim.io',
			'api-demo.wizim.io',
			'api2.wizim.io',
		],
		replaysSessionSampleRate: parseSampleRate(
			import.meta.env.VITE_SENTRY_REPLAYS_SESSION_SAMPLE_RATE
		),
		replaysOnErrorSampleRate: parseSampleRate(
			import.meta.env.VITE_SENTRY_REPLAYS_ON_ERROR_SAMPLE_RATE
		),

		ignoreErrors: [
			'Failed to fetch dynamically imported module',
			'Importing a module script failed',
			'Request failed with status code 404',
			'Request failed with status code 400',
			'No error message',
		],

		// Before send event
		beforeSend: filterSentryData,

		// Before send transaction
		beforeSendTransaction: filterSentryData,
	});
}

app.use(router);
app.use(i18n);

const pinia = createPinia();
pinia.use(
	Sentry.createSentryPiniaPlugin({
		// biome-ignore lint/suspicious/noExplicitAny: Init Biome
		stateTransformer: (state: any) => {
			if (state?.auth?.state?.token) {
				state.auth.state.token = '[Filtered]';
			}
			return state;
		},
	})
);
app.use(pinia);

app.use(ToastPlugin);
app.use(PerfectScrollbarPlugin);

app.use(VueScrollTo);
app.use(VueTheMask);

app.use(VueVirtualScroller);

app.component('GoogleMap', GoogleMap);
app.component('GoogleMapMarker', Marker);

import { default as TableColumn } from 'primevue/column';
import ColumnGroup from 'primevue/columngroup';
// [PrimeVue integration] - START
import PrimeVue from 'primevue/config';
import DataTable from 'primevue/datatable';

// import 'primevue/resources/themes/lara-light-indigo/theme.css';
// import 'primevue/resources/primevue.min.css';

app.use(PrimeVue, { unstyled: true });
app.component('TableColumn', TableColumn);
app.component('ColumnGroup', ColumnGroup);
app.component('DataTable', DataTable);

// [PrimeVue integration] - END

import Action from '@/components/Action.vue';
import Card from '@/components/Card.vue';
import ErrorComponent from '@/components/form/Error.vue';
import Field from '@/components/form/Field.vue';
import FormContainer from '@/components/form/FormContainer.vue';
import FormView from '@/components/form/FormView.vue';
import GlobalErrors from '@/components/form/GlobalErrors.vue';
import Column from '@/components/grid/Column.vue';
import Row from '@/components/grid/Row.vue';
import Icon from '@/components/Icon.vue';
import LinkLike from '@/components/LinkLike.vue';
import Pulse from '@/components/loader/Pulse.vue';

app.component('pulse', Pulse);
app.component('row', Row);
app.component('column', Column);
app.component('action', Action);
app.component('card', Card);
app.component('form-container', FormContainer);
app.component('form-view', FormView);
app.component('field', Field);
app.component('global-errors', GlobalErrors);
app.component('error', ErrorComponent);
app.component('link-like', LinkLike);
app.component('icon', Icon);

moment.locale('fr', {
	months: [
		'janvier',
		'février',
		'mars',
		'avril',
		'mai',
		'juin',
		'juillet',
		'août',
		'septembre',
		'octobre',
		'novembre',
		'décembre',
	],
	monthsShort: [
		'janv',
		'févr',
		'mars',
		'avr',
		'mai',
		'juin',
		'juil',
		'août',
		'sept',
		'oct',
		'nov',
		'déc',
	],
	weekdays: [
		'dimanche',
		'lundi',
		'mardi',
		'mercredi',
		'jeudi',
		'vendredi',
		'samedi',
	],
	weekdaysShort: ['dim', 'lun', 'mar', 'mer', 'jeu', 'ven', 'sam'],
	weekdaysMin: ['di', 'lu', 'ma', 'me', 'je', 've', 'sa'],
	longDateFormat: {
		L: 'DD/MM/YYYY',
		LL: 'D MMMM YYYY',
		LLL: 'D MMMM YYYY HH:mm',
		LLLL: 'dddd D MMMM YYYY HH:mm',
		LTS: 'HH:mm:ss',
		LT: 'HH:mm',
	},
	week: {
		dow: 1, // monday is the first day of the week
		doy: 4, // the week containing the 4th of january is the first week of the year
	},
});

app.use(VueMatomo, {
	host: 'https://matomo.wizim.io',
	siteId: import.meta.env.VITE_MATOMO_SITE_ID,
	trackerFileName: 'matomo',
	enableLinkTracking: true,
	trackInitialView: true,
	enableHeartBeatTimer: true,
	debug: import.meta.env.DEV,
});

app.mount('#app');
